/**
 * Reusable catalog filter panel + the client-side filter application logic.
 *
 * Used in:
 *   - CategoryPage.jsx (main catalogue browsing)
 *   - TryOnExperimentPage.jsx (choose-product step for try-on)
 *
 * The two surfaces share the SAME UI and the SAME filtering rules to keep
 * the experience consistent. Only the click action on each product card
 * differs (PDP vs. start try-on) — that lives in the parent.
 *
 * Shape of the filter object:
 *   { brand, category, style, occasion, forWhom, combines,
 *     sophMin, sophMax, minPrice, maxPrice }
 */
import { CATALOG_BRAND_FILTER_OPTIONS, CATEGORIES, STYLES, OCCASIONS, FOR_WHOM, COMBINES } from "../lib/api";

export const EMPTY_FILTERS = Object.freeze({
  brand: "",
  category: "",
  style: "",
  occasion: "",
  forWhom: "",
  combines: "",
  sophMin: 1,
  sophMax: 5,
  minPrice: "",
  maxPrice: "",
});

/**
 * Apply every client-side filter to a list of products. Server already
 * narrowed by brand + category before this point; the rest is local.
 */
export function applyCatalogFilters(products, filters) {
  let data = products;
  if (filters.style) data = data.filter((p) => p.style?.includes(filters.style));
  if (filters.occasion) data = data.filter((p) => p.occasion?.includes(filters.occasion));
  if (filters.forWhom) data = data.filter((p) => p.for_whom?.includes(filters.forWhom));
  if (filters.combines) data = data.filter((p) => p.combines_with?.includes(filters.combines));
  if (filters.sophMin > 1 || filters.sophMax < 5) {
    data = data.filter((p) => {
      const s = Number(p.sophistication);
      if (!s) return false;
      return s >= filters.sophMin && s <= filters.sophMax;
    });
  }
  if (filters.minPrice) data = data.filter((p) => p.price >= +filters.minPrice);
  if (filters.maxPrice) data = data.filter((p) => p.price <= +filters.maxPrice);
  return data;
}

/**
 * Optional category dropdown: pass `showCategory={true}` when the host page
 * isn't already locked to a specific category (e.g. /joias/todos & try-on
 * picker), and `false` on category-specific routes (e.g. /joias/Anéis) where
 * a category dropdown would be misleading.
 */
export default function CatalogFilterPanel({ filters, setFilters, showCategory, onClear }) {
  return (
    <div className="space-y-7">
      <FilterSelect label="Marca" value={filters.brand} options={CATALOG_BRAND_FILTER_OPTIONS} onChange={(v) => setFilters({ ...filters, brand: v })} testid="filter-brand" />
      {showCategory && (
        <FilterSelect
          label="Categoria"
          value={filters.category}
          options={CATEGORIES.map((c) => c.label)}
          onChange={(v) => setFilters({ ...filters, category: v })}
          testid="filter-category"
        />
      )}
      <FilterSelect label="Estilo" value={filters.style} options={STYLES} onChange={(v) => setFilters({ ...filters, style: v })} testid="filter-style" />
      <FilterSelect label="Ocasião" value={filters.occasion} options={OCCASIONS} onChange={(v) => setFilters({ ...filters, occasion: v })} testid="filter-occasion" />
      <FilterSelect label="Para quem" value={filters.forWhom} options={FOR_WHOM} onChange={(v) => setFilters({ ...filters, forWhom: v })} testid="filter-for-whom" />
      <FilterSelect label="Combina com" value={filters.combines} options={COMBINES} onChange={(v) => setFilters({ ...filters, combines: v })} testid="filter-combines" />
      <SophisticationFilter
        min={filters.sophMin}
        max={filters.sophMax}
        onChange={(sophMin, sophMax) => setFilters({ ...filters, sophMin, sophMax })}
      />
      <div>
        <p className="overline text-[#A8A8A8] mb-3">Preço</p>
        <div className="flex items-center gap-2">
          <input
            type="number"
            placeholder="Min"
            value={filters.minPrice}
            onChange={(e) => setFilters({ ...filters, minPrice: e.target.value })}
            className="w-full border border-[#E8E4DF] px-3 py-2 text-sm focus:border-[#B8A88A] outline-none"
            data-testid="filter-price-min"
          />
          <span className="text-[#A8A8A8]">—</span>
          <input
            type="number"
            placeholder="Max"
            value={filters.maxPrice}
            onChange={(e) => setFilters({ ...filters, maxPrice: e.target.value })}
            className="w-full border border-[#E8E4DF] px-3 py-2 text-sm focus:border-[#B8A88A] outline-none"
            data-testid="filter-price-max"
          />
        </div>
      </div>
      <button onClick={onClear} className="overline text-[#B8A88A] hover:text-[#8C7B5E]" data-testid="clear-filters">
        Limpar filtros
      </button>
    </div>
  );
}

function FilterSelect({ label, value, options, onChange, testid }) {
  return (
    <div>
      <p className="overline text-[#A8A8A8] mb-3">{label}</p>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full border border-[#E8E4DF] px-3 py-2 text-sm bg-white focus:border-[#B8A88A] outline-none"
        data-testid={testid}
      >
        <option value="">Todos</option>
        {options.map((o) => <option key={o} value={o}>{o}</option>)}
      </select>
    </div>
  );
}

function SophisticationFilter({ min, max, onChange }) {
  const labels = { 1: "Sutil", 2: "Discreto", 3: "Equilibrado", 4: "Marcante", 5: "Statement" };
  return (
    <div data-testid="filter-sophistication">
      <p className="overline text-[#A8A8A8] mb-3">Sofisticação</p>
      <div className="text-xs text-[#6B6B6B] flex items-center justify-between mb-2">
        <span>{min}. {labels[min]}</span>
        <span className="text-[#A8A8A8]">→</span>
        <span>{max}. {labels[max]}</span>
      </div>
      <div className="space-y-2">
        <input
          type="range" min={1} max={5} step={1} value={min}
          onChange={(e) => {
            const v = Math.min(Number(e.target.value), max);
            onChange(v, max);
          }}
          className="w-full accent-[#B8A88A]"
          data-testid="filter-sophistication-min"
        />
        <input
          type="range" min={1} max={5} step={1} value={max}
          onChange={(e) => {
            const v = Math.max(Number(e.target.value), min);
            onChange(min, v);
          }}
          className="w-full accent-[#B8A88A]"
          data-testid="filter-sophistication-max"
        />
      </div>
    </div>
  );
}
